import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { useToolbarElementsWithout } from '@Components/Tables/useToolbarElementsWithout';
import { AUFTRAG_API } from '@Constants/backendConstants';
import { Permission } from '@Constants/Permission.g';
import { getStorageTransactionColumns, initialStorageTransactionSorting } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useListApi } from '@Hooks/useListApi';
import type { ApiStorageTransaction } from '@Model';
import { EnumFrontendLayoutSetting } from '@Model';
import { StorageTransactionsRepository } from '@Repositories/Auftragsbearbeitung';
import {
    Button,
    iconSizeXs,
    type ListRequest,
    type ToolbarElements,
    type ToolbarOptions,
    useTableRefetch,
} from '@synqo/frontend-component-library';
import { IconX } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
    mapEmptyAvvFilter,
    scopeConditions,
    scopeLabel,
    scopePrefill,
    STORAGE_JOURNAL_TABLE,
    withConditions,
    withoutScope,
    withCreationTieBreak,
} from './storageJournalFilters';
import { StorageTransactionOrigin } from './StorageTransactionOrigin';
import { useOpenRelatedBooking } from './useOpenRelatedBooking';
import { useOpenStorageTransaction } from './useOpenStorageTransaction';

const HIDE_ADD: ToolbarOptions[] = ['add'];

const renderOrigin = (booking: ApiStorageTransaction) => <StorageTransactionOrigin booking={booking} />;

// @ai-generated 2026-10-23T18:10:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function FrameListStorageTransaction() {
    const hasPermission = useHasPermission();
    const canCreate = hasPermission(Permission.AuftragsbearbeitungStorageTransactionsCreate);

    const module = useListApi(StorageTransactionsRepository, AUFTRAG_API);
    const { openBooking, openView } = useOpenStorageTransaction();
    const openRelatedBooking = useOpenRelatedBooking();
    const { refetchRef, refetchTable } = useTableRefetch();
    const baseToolbarElements = useToolbarElementsWithout(undefined, canCreate ? undefined : HIDE_ADD);
    const [searchParams, setSearchParams] = useSearchParams();
    const scope = useMemo(() => scopeConditions(searchParams), [searchParams]);
    const label = scopeLabel(searchParams);
    const prefill = useMemo(() => scopePrefill(searchParams), [searchParams]);

    const toolbarElements = useMemo<ToolbarElements>(
        () => (standardOptions) => {
            const options =
                typeof baseToolbarElements === 'function'
                    ? baseToolbarElements(standardOptions)
                    : baseToolbarElements ?? standardOptions;
            if (scope.length === 0) {
                return options;
            }
            const clear = () => setSearchParams(withoutScope(searchParams));
            return [
                <Button key="scope" size="xs" appearance="outlined" onClick={clear}>
                    {label}&nbsp;
                    <IconX size={iconSizeXs} />
                </Button>,
                ...options,
            ];
        },
        [baseToolbarElements, scope, label, searchParams, setSearchParams],
    );

    const loadPage = useCallback(
        (request: ListRequest) =>
            module.loadPage(withConditions(mapEmptyAvvFilter(withCreationTieBreak(request)), scope)),
        [module, scope],
    );
    const book = useCallback(async () => {
        await openBooking({ ...prefill, onBooked: refetchTable });
        return true;
    }, [openBooking, prefill, refetchTable]);
    const view = useCallback(
        (booking: ApiStorageTransaction) => {
            openView(booking.id, { onCancelled: refetchTable });
            return true;
        },
        [openView, refetchTable],
    );
    const columns = useMemo(
        () =>
            getStorageTransactionColumns(
                (booking) => void openRelatedBooking(booking, { onCancelled: refetchTable }),
                renderOrigin,
            ),
        [openRelatedBooking, refetchTable],
    );

    return (
        <TableWithStoredSettingsAndLayoutSettings<ApiStorageTransaction>
            key={searchParams.toString()}
            layoutSettingsContext={EnumFrontendLayoutSetting.StorageTransactionsTableLayout}
            name={STORAGE_JOURNAL_TABLE}
            columns={columns}
            initialSorting={initialStorageTransactionSorting}
            loader={loadPage}
            refetchRef={refetchRef}
            canCreate={canCreate}
            onAdd={canCreate ? book : undefined}
            addLabel="Neue Lagerbuchung"
            toolbarElements={toolbarElements}
            onSelect={view}
            fullSize
        />
    );
}
